import { Component, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import "@/App.css";
import { SiteProvider, useSite } from "@/context/SiteContext";
import { AuthProvider } from "@/context/AuthContext";
import { IntroProvider, useIntro } from "@/context/IntroContext";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import CookieBanner from "@/components/CookieBanner";
import Preloader from "@/components/Preloader";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import ServiceDetail from "@/pages/ServiceDetail";
import About from "@/pages/About";
import Samples from "@/pages/Samples";
import TestimonialsPage from "@/pages/TestimonialsPage";
import FAQPage from "@/pages/FAQPage";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";

import AdminLogin from "@/admin/Login";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/Dashboard";
import ContentEditor from "@/admin/ContentEditor";
import CollectionManager from "@/admin/CollectionManager";
import LeadsManager from "@/admin/Leads";
import MediaLibrary from "@/admin/MediaLibrary";
import SEOManager from "@/admin/SEOManager";
import AnalyticsManager from "@/admin/AnalyticsManager";
import SettingsManager from "@/admin/SettingsManager";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0B0B14] text-white p-6">
          <div className="glass rounded-2xl p-8 max-w-md text-center">
            <h1 className="font-display font-bold text-xl mb-2">Something went wrong</h1>
            <p className="text-sm text-mist mb-4">Please refresh the page. If the problem persists, contact support.</p>
            <button onClick={() => window.location.reload()} className="px-5 py-2.5 rounded-full bg-accent text-white text-sm font-semibold" data-testid="error-reload-btn">
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicShell({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) return <>{children}</>;
  return (
    <>
      <Navbar />
      <main className="min-h-screen">{children}</main>
      <Footer />
      <FloatingActions />
    </>
  );
}

function IntroGate() {
  const { pathname } = useLocation();
  const { finish } = useIntro();
  const [show, setShow] = useState(() => !pathname.startsWith("/admin"));
  const doneRef = useRef(false);

  const complete = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    finish();
    setShow(false);
  };

  useEffect(() => {
    if (pathname.startsWith("/admin")) {
      finish();
      return undefined;
    }
    const fallback = setTimeout(complete, 10000);
    return () => clearTimeout(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AnimatePresence>{show && <Preloader onComplete={complete} />}</AnimatePresence>;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SiteProvider>
          <BrowserRouter>
            <IntroProvider>
              <ScrollToTop />
              <SEO />
              <IntroGate />
              <SmoothScroll>
              <PublicShell>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/samples" element={<Samples />} />
                  <Route path="/testimonials" element={<TestimonialsPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy" element={<Legal kind="privacy" />} />
                  <Route path="/terms" element={<Legal kind="terms" />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="content" element={<ContentEditor />} />
                    <Route path="services" element={<CollectionManager key="services" name="services" />} />
                    <Route path="samples" element={<CollectionManager key="samples" name="samples" />} />
                    <Route path="ugc" element={<CollectionManager key="ugc_models" name="ugc_models" />} />
                    <Route path="proofs" element={<CollectionManager key="proofs" name="proofs" />} />
                    <Route path="testimonials" element={<CollectionManager key="testimonials" name="testimonials" />} />
                    <Route path="faqs" element={<CollectionManager key="faqs" name="faqs" />} />
                    <Route path="leads" element={<LeadsManager />} />
                    <Route path="media" element={<MediaLibrary />} />
                    <Route path="seo" element={<SEOManager />} />
                    <Route path="analytics" element={<AnalyticsManager />} />
                    <Route path="settings" element={<SettingsManager />} />
                  </Route>
                  <Route path="*" element={<Home />} />
                </Routes>
              </PublicShell>
              <CookieBanner />
              <div className="noise-grain" aria-hidden="true" />
            </SmoothScroll>
            </IntroProvider>
            <Toaster position="bottom-left" theme="dark" richColors closeButton />
          </BrowserRouter>
        </SiteProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
